import { useState, useMemo, useCallback, useRef, useEffect } from "react"
import type { GuidebookPlace, PlaceCategory } from "./types"
import { PLACE_CATEGORIES, getCategoryForType } from "./types"
import { PlaceCard } from "./PlaceCard"
import { PlaceDetailSheet } from "./PlaceDetailSheet"

interface Props {
  places: GuidebookPlace[]
  /** When set, immediately opens this place's detail sheet (used by HighlightsStrip). */
  externalOpenPlace?: GuidebookPlace | null
  /** Called after externalOpenPlace has been handled so parent can reset the value. */
  onExternalOpenHandled?: () => void
  /** Voice-aware section heading. Falls back to "Places we love". */
  sectionTitle?: string
}

const BEST_FOR_OPTIONS = ["Solo", "Couple", "Family", "Group"] as const
const COLLECTION_PREVIEW_COUNT = 4

export function PlacesSection({ places, externalOpenPlace, onExternalOpenHandled, sectionTitle }: Props) {
  const [activeArea, setActiveArea] = useState<string>("")
  const [activeBestFor, setActiveBestFor] = useState<string>("")
  const [expandedCategories, setExpandedCategories] = useState<Set<PlaceCategory>>(new Set())
  const [showFilters, setShowFilters] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState<GuidebookPlace | null>(null)
  const detailDialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (externalOpenPlace) {
      setSelectedPlace(externalOpenPlace)
      detailDialogRef.current?.showModal()
      onExternalOpenHandled?.()
    }
  }, [externalOpenPlace, onExternalOpenHandled])

  const categorized = useMemo(() => {
    const map = new Map<PlaceCategory, GuidebookPlace[]>()
    for (const cat of PLACE_CATEGORIES) {
      map.set(cat, [])
    }
    for (const p of places) {
      const cat = getCategoryForType(p.type || "")
      map.get(cat)!.push(p)
    }
    return map
  }, [places])

  const areas = useMemo(() => {
    const set = new Set<string>()
    for (const p of places) {
      if (p.area) set.add(p.area)
    }
    return Array.from(set).sort()
  }, [places])

  const filterPlace = useCallback(
    (p: GuidebookPlace): boolean => {
      if (activeArea && p.area !== activeArea) return false
      if (activeBestFor) {
        const suitable = p.suitableFor || []
        const bestForField = (p.en?.bestFor || "").toLowerCase()
        const match =
          suitable.some((s) => s.toLowerCase() === activeBestFor.toLowerCase()) ||
          bestForField.includes(activeBestFor.toLowerCase())
        if (!match) return false
      }
      return true
    },
    [activeArea, activeBestFor],
  )

  const toggleCategory = useCallback((cat: PlaceCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }, [])

  const openDetail = useCallback((place: GuidebookPlace) => {
    setSelectedPlace(place)
    detailDialogRef.current?.showModal()
  }, [])

  const closeDetail = useCallback(() => {
    detailDialogRef.current?.close()
    setSelectedPlace(null)
  }, [])

  const hasActiveFilters = activeArea !== "" || activeBestFor !== ""

  const clearFilters = useCallback(() => {
    setActiveArea("")
    setActiveBestFor("")
  }, [])

  if (places.length === 0) return null

  return (
    <div className="gb-section">
      <h2 className="gb-section__title">{sectionTitle || "Places we love"}</h2>
      <p className="gb-section__subtitle">Hand-picked by your hosts, refreshed for today</p>

      <div className="gb-places__filter-bar">
        <button
          className={`gb-places__filter-toggle${showFilters ? " gb-places__filter-toggle--active" : ""}`}
          onClick={() => setShowFilters(!showFilters)}
          type="button"
          aria-expanded={showFilters}
        >
          Filter
        </button>
        {hasActiveFilters && !showFilters && (
          <>
            {activeArea && (
              <span className="gb-places__active-chip">
                {activeArea}
                <button
                  className="gb-places__active-chip-remove"
                  onClick={() => setActiveArea("")}
                  type="button"
                  aria-label={`Remove ${activeArea} filter`}
                >
                  &times;
                </button>
              </span>
            )}
            {activeBestFor && (
              <span className="gb-places__active-chip">
                {activeBestFor}
                <button
                  className="gb-places__active-chip-remove"
                  onClick={() => setActiveBestFor("")}
                  type="button"
                  aria-label={`Remove ${activeBestFor} filter`}
                >
                  &times;
                </button>
              </span>
            )}
            <button
              className="gb-places__filter-clear"
              onClick={clearFilters}
              type="button"
            >
              Clear all
            </button>
          </>
        )}
      </div>

      {showFilters && (
        <div className="gb-places__filters">
          {areas.length > 1 && (
            <select
              className="gb-places__filter-select"
              value={activeArea}
              onChange={(e) => setActiveArea(e.target.value)}
              aria-label="Filter by area"
            >
              <option value="">All areas</option>
              {areas.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          )}
          {BEST_FOR_OPTIONS.map((opt) => (
            <button
              key={opt}
              className={`gb-places__filter-pill${activeBestFor === opt ? " gb-places__filter-pill--active" : ""}`}
              onClick={() => setActiveBestFor(activeBestFor === opt ? "" : opt)}
              type="button"
            >
              {opt}
            </button>
          ))}
        </div>
      )}

      {PLACE_CATEGORIES.map((cat) => {
        const catPlaces = (categorized.get(cat) || []).filter(filterPlace)
        if (catPlaces.length === 0) return null

        const isExpanded = expandedCategories.has(cat)
        const displayPlaces = isExpanded ? catPlaces : catPlaces.slice(0, COLLECTION_PREVIEW_COUNT)
        const hasMore = catPlaces.length > COLLECTION_PREVIEW_COUNT

        return (
          <div className="gb-collection" key={cat}>
            <h3 className="gb-collection__title">{cat}</h3>
            <div className="gb-collection__grid">
              {displayPlaces.map((p) => (
                <PlaceCard key={p.id} place={p} onClick={() => openDetail(p)} />
              ))}
            </div>
            {hasMore && (
              <button
                className="gb-collection__expand"
                onClick={() => toggleCategory(cat)}
                type="button"
              >
                {isExpanded ? "Show less" : `See all ${catPlaces.length} places`}
              </button>
            )}
          </div>
        )
      })}

      <PlaceDetailSheet place={selectedPlace} dialogRef={detailDialogRef} onClose={closeDetail} />
    </div>
  )
}
